const CHANNEL_NAME = 'koral-krush-cart-v2';
const FALLBACK_STORAGE_KEY = 'koral_krush_cart_sync_v2';

function defaultWindow() {
  return typeof window === 'undefined' ? null : window;
}

function sourceIdentifier(windowObject) {
  try {
    const value = windowObject?.crypto?.randomUUID?.();
    if (value) return value;
  } catch {
    // A notification identifier is not a trust boundary.
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Cross-tab invalidation only. IndexedDB remains the source of truth, so a
 * lost, duplicated, forged, or reordered notification can never mutate cart
 * data. Receivers always reload the latest committed cart transactionally.
 */
export function createCartSyncNotifier({ windowObject = defaultWindow() } = {}) {
  const listeners = new Set();
  const sourceId = sourceIdentifier(windowObject);
  let sequence = 0;
  let channel = null;
  let listening = false;

  const dispatch = () => {
    listeners.forEach(listener => {
      try {
        listener();
      } catch {
        // One React tree must not prevent other subscribers from refreshing.
      }
    });
  };

  const handleChannelMessage = event => {
    if (event?.data?.channel === CHANNEL_NAME && event.data.sourceId !== sourceId) {
      dispatch();
    }
  };
  const handleStorage = event => {
    if (event?.key === FALLBACK_STORAGE_KEY && event.newValue) dispatch();
  };

  const startTransports = () => {
    if (listening) return;
    listening = true;
    try {
      if (typeof windowObject?.BroadcastChannel === 'function') {
        channel = new windowObject.BroadcastChannel(CHANNEL_NAME);
        channel.addEventListener('message', handleChannelMessage);
      }
    } catch {
      channel = null;
    }
    try {
      windowObject?.addEventListener?.('storage', handleStorage);
    } catch {
      // The direct mutation response still keeps this tab current.
    }
  };

  const stopTransports = () => {
    if (!listening) return;
    listening = false;
    try {
      channel?.removeEventListener('message', handleChannelMessage);
      channel?.close();
      windowObject?.removeEventListener?.('storage', handleStorage);
    } catch {
      // Closing a notification transport is best effort.
    }
    channel = null;
  };

  return {
    subscribe(listener) {
      if (typeof listener !== 'function') return () => {};
      if (listeners.size === 0) startTransports();
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stopTransports();
      };
    },
    publish() {
      sequence += 1;
      const notification = { channel: CHANNEL_NAME, sourceId, sequence };
      try {
        channel?.postMessage(notification);
      } catch {
        // Continue with the storage-event fallback.
      }
      try {
        const storage = windowObject?.localStorage;
        if (storage) {
          storage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(notification));
          storage.removeItem(FALLBACK_STORAGE_KEY);
        }
      } catch {
        // Notifications are opportunistic; the committed IndexedDB data is safe.
      }
    },
    close() {
      listeners.clear();
      stopTransports();
    },
  };
}
